import { useEffect, useState } from "react";
import { api, ApiError, type OpeningWindow } from "../lib/api.ts";
import { dayName, trimTime } from "../lib/format.ts";

interface HoursEditorProps {
  token: string;
  onUnauthorised: () => void;
}

/** Monday first, as a UK week is read. */
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

/** Sensible default when opening a day that was closed. */
const DEFAULT_WINDOW = { openTime: "09:00", closeTime: "17:00" };

type Draft = Record<number, Array<{ openTime: string; closeTime: string }>>;

function toDraft(windows: OpeningWindow[]): Draft {
  const draft: Draft = {};
  for (const day of DAY_ORDER) draft[day] = [];
  for (const w of windows) {
    (draft[w.dayOfWeek] ??= []).push({
      openTime: trimTime(w.openTime),
      closeTime: trimTime(w.closeTime),
    });
  }
  for (const day of DAY_ORDER) {
    draft[day]!.sort((a, b) => a.openTime.localeCompare(b.openTime));
  }
  return draft;
}

function toWindows(draft: Draft): OpeningWindow[] {
  return DAY_ORDER.flatMap((dayOfWeek) =>
    (draft[dayOfWeek] ?? []).map((w) => ({ dayOfWeek, ...w })),
  );
}

/** Problems worth blocking the save for, keyed by day. */
function validate(draft: Draft): Record<number, string> {
  const errors: Record<number, string> = {};

  for (const day of DAY_ORDER) {
    const windows = [...(draft[day] ?? [])].sort((a, b) =>
      a.openTime.localeCompare(b.openTime),
    );

    for (const w of windows) {
      if (w.closeTime <= w.openTime) {
        errors[day] = "Closing time must be after opening time.";
      }
    }
    // Touching is fine (13:00-13:00); genuinely overlapping is not, because the
    // picker would offer the same time twice.
    for (let i = 1; i < windows.length; i += 1) {
      if (windows[i]!.openTime < windows[i - 1]!.closeTime) {
        errors[day] = "Two periods overlap. Adjust the times or merge them.";
      }
    }
  }

  return errors;
}

export function HoursEditor({ token, onUnauthorised }: HoursEditorProps) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saved, setSaved] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.admin
      .getHours(token)
      .then((result) => {
        const next = toDraft(result.windows);
        setDraft(next);
        setSaved(next);
      })
      .catch((caught: unknown) => {
        if (caught instanceof ApiError && caught.status === 401)
          return onUnauthorised();
        setError("Opening hours could not be loaded.");
      });
  }, [token, onUnauthorised]);

  function mutate(
    dayOfWeek: number,
    next: Array<{ openTime: string; closeTime: string }>,
  ) {
    setDraft((current) =>
      current ? { ...current, [dayOfWeek]: next } : current,
    );
    setMessage(null);
  }

  function addWindow(dayOfWeek: number) {
    const existing = draft?.[dayOfWeek] ?? [];
    const last = existing.at(-1);
    // Start the new period an hour after the previous one ends, so consecutive
    // additions do not immediately collide.
    const openTime = last
      ? bumpHour(last.closeTime, 1)
      : DEFAULT_WINDOW.openTime;
    const closeTime = last
      ? bumpHour(last.closeTime, 4)
      : DEFAULT_WINDOW.closeTime;
    mutate(dayOfWeek, [...existing, { openTime, closeTime }]);
  }

  function bumpHour(time: string, hours: number): string {
    const [h, m] = time.split(":").map(Number) as [number, number];
    const capped = Math.min(23, h + hours);
    return `${String(capped).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }

  const errors = draft ? validate(draft) : {};
  const hasErrors = Object.keys(errors).length > 0;
  const dirty =
    draft !== null &&
    saved !== null &&
    JSON.stringify(draft) !== JSON.stringify(saved);

  async function save() {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.admin.saveHours(token, toWindows(draft));
      const next = toDraft(result.windows);
      setDraft(next);
      setSaved(next);
      setMessage(
        "Opening hours saved. The website and booking page are updated.",
      );
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      setError(
        caught instanceof ApiError
          ? caught.message
          : "The hours could not be saved.",
      );
    } finally {
      setBusy(false);
    }
  }

  if (!draft) {
    return (
      <section className="page">
        {error ? (
          <p className="notice notice--error">{error}</p>
        ) : (
          <p className="booking__loading">
            <span className="spinner" /> Loading hours…
          </p>
        )}
      </section>
    );
  }

  return (
    <section className="page stack-lg">
      <div>
        <h2 className="admin__section-title">Opening hours</h2>
        <p className="field-hint">
          Add as many periods per day as you need - a morning and an afternoon
          either side of lunch, for instance. A day with no periods is closed.
          Changes take effect immediately; an appointment already booked outside
          the new hours is not cancelled.
        </p>
      </div>

      <div className="hours-editor">
        {DAY_ORDER.map((dayOfWeek) => {
          const windows = draft[dayOfWeek] ?? [];
          const dayError = errors[dayOfWeek];

          return (
            <div
              key={dayOfWeek}
              className={`day-card ${windows.length === 0 ? "day-card--closed" : ""}`}
            >
              <div className="day-card__head">
                <h3 className="day-card__name">{dayName(dayOfWeek)}</h3>
                {windows.length === 0 && (
                  <span className="day-card__closed-tag">Closed</span>
                )}
                <button
                  type="button"
                  className="btn btn--quiet day-card__add"
                  onClick={() => addWindow(dayOfWeek)}
                >
                  + Add period
                </button>
              </div>

              {windows.length > 0 && (
                <ul className="window-list">
                  {windows.map((window, index) => (
                    <li key={index} className="window-row">
                      <input
                        type="time"
                        className="input"
                        value={window.openTime}
                        step={900}
                        aria-label={`${dayName(dayOfWeek)} period ${index + 1} opening time`}
                        onChange={(event) =>
                          mutate(
                            dayOfWeek,
                            windows.map((w, i) =>
                              i === index
                                ? { ...w, openTime: event.target.value }
                                : w,
                            ),
                          )
                        }
                      />
                      <span aria-hidden="true">–</span>
                      <input
                        type="time"
                        className="input"
                        value={window.closeTime}
                        step={900}
                        aria-label={`${dayName(dayOfWeek)} period ${index + 1} closing time`}
                        onChange={(event) =>
                          mutate(
                            dayOfWeek,
                            windows.map((w, i) =>
                              i === index
                                ? { ...w, closeTime: event.target.value }
                                : w,
                            ),
                          )
                        }
                      />
                      <button
                        type="button"
                        className="btn btn--danger window-row__remove"
                        aria-label={`Remove ${dayName(dayOfWeek)} period ${index + 1}`}
                        onClick={() =>
                          mutate(
                            dayOfWeek,
                            windows.filter((_, i) => i !== index),
                          )
                        }
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {dayError && <p className="field-error">{dayError}</p>}
            </div>
          );
        })}
      </div>

      {message && <p className="notice notice--success">{message}</p>}
      {error && <p className="notice notice--error">{error}</p>}

      <div className="row">
        <button
          type="button"
          className="btn"
          onClick={save}
          disabled={busy || !dirty || hasErrors}
        >
          {busy ? <span className="spinner" /> : "Save hours"}
        </button>
        {dirty && (
          <button
            type="button"
            className="btn btn--quiet"
            onClick={() => setDraft(saved)}
          >
            Discard changes
          </button>
        )}
      </div>
    </section>
  );
}
